import { useRef, useCallback, useEffect } from 'react'

export const useHorizontalDragScroll = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollLeft = useRef(0)

  const handleMouseDown = useCallback((e: MouseEvent) => {
    const container = containerRef.current
    if (!container) return

    // Only activate if there's horizontal overflow
    if (container.scrollWidth <= container.clientWidth) return

    // Don't interfere with interactive elements
    const target = e.target as HTMLElement
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('select') ||
      target.closest('[role="button"]')
    )
      return

    isDragging.current = true
    startX.current = e.pageX - container.offsetLeft
    scrollLeft.current = container.scrollLeft
    container.style.cursor = 'grabbing'
    container.style.userSelect = 'none'
  }, [])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current) return
    const container = containerRef.current
    if (!container) return

    e.preventDefault()
    const x = e.pageX - container.offsetLeft
    const walk = x - startX.current
    container.scrollLeft = scrollLeft.current - walk
  }, [])

  const handleMouseUp = useCallback(() => {
    isDragging.current = false
    const container = containerRef.current
    if (container) {
      container.style.cursor = ''
      container.style.userSelect = ''
    }
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      container.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseDown, handleMouseMove, handleMouseUp])

  return containerRef
}
